import { useTranslation } from 'react-i18next';
import {
  BackgroundJob,
  BackgroundJobStatusEnum,
  useBackgroundJobCancelMutation,
  useBackgroundJobDismissMutation,
  useBackgroundJobRequestDeliveryMutation,
  useBackgroundJobRequeueMutation,
} from 'graphql-common';
import toast from '@lib/components/Toast/Toast';
import { getErrorMessage } from '@lib/utils/graphQLErrors';
import { logger } from '@lib/services/logger';

type Args = {
  job: BackgroundJob;
  removeJobId: (id: string) => void;
};

export default function useActions({ job, removeJobId }: Args) {
  const {
    id,
    status,
    canCancel,
    canDismiss,
    canRequestDelivery,
    canRequeue,
    deliveryRequestedAt,
  } = job;
  const { t } = useTranslation();

  const isRunning =
    status === BackgroundJobStatusEnum.Queued ||
    status === BackgroundJobStatusEnum.Working ||
    status === BackgroundJobStatusEnum.Retrying;

  const canCancelJob = isRunning && !!canCancel?.value;
  const canDismissJob = !isRunning && !!canDismiss?.value;

  const [cancelMutation, cancelMutationResult] =
    useBackgroundJobCancelMutation();
  const [dismissMutation, dismissMutationResult] =
    useBackgroundJobDismissMutation();
  const [requestDeliveryMutation, requestDeliveryMutationResult] =
    useBackgroundJobRequestDeliveryMutation();
  const [requeueMutation, requeueMutationResult] =
    useBackgroundJobRequeueMutation();

  const { loading: cancelLoading } = cancelMutationResult;
  const { loading: dismissLoading } = dismissMutationResult;
  const { loading: requestDeliveryLoading } = requestDeliveryMutationResult;
  const { loading: requeueLoading } = requeueMutationResult;

  // "Send by email" — emails the job's result. Gate purely on server state:
  // `canRequestDelivery.value` (can request now) or `deliveryRequestedAt`
  // (already requested → show as sent). Both are false on e.g. a FAILED job, so
  // the button is hidden there — don't keep it alive with a sticky local flag.
  const deliveryRequested = !!deliveryRequestedAt;
  const canSendByEmail = !!canRequestDelivery?.value || deliveryRequested;

  const canRetry = !!canRequeue?.value;

  // Cancel a running job (transitions to Stopped, stays visible) or dismiss a
  // finished one (removed from the list).
  const onRemoveHandler = () => {
    if (canCancelJob) {
      cancelMutation({
        variables: { input: { id } },
        onCompleted: (res) => {
          logger.debug('✅ Cancel background job', { data: res?.data });
        },
        onError: (error) => {
          toast({ content: getErrorMessage(error, t) });
        },
      });
    } else if (canDismissJob) {
      dismissMutation({
        variables: { input: { id } },
        onCompleted: () => {
          removeJobId(id);
        },
        onError: (error) => {
          toast({ content: getErrorMessage(error, t) });
        },
      });
    }
  };

  const onSendByEmailHandler = () => {
    requestDeliveryMutation({
      variables: { input: { id } },
      onCompleted: (res) => {
        logger.debug('✅ Request job delivery', { data: res?.data });
      },
      onError: (error) => {
        toast({ content: getErrorMessage(error, t) });
      },
    });
  };

  const onRetryHandler = () => {
    requeueMutation({
      variables: { input: { id } },
      onCompleted: (res) => {
        logger.debug('✅ Requeue background job', { data: res?.data });
      },
      onError: (error) => {
        toast({ content: getErrorMessage(error, t) });
      },
    });
  };

  return {
    onRemoveHandler,
    canCancelJob,
    canDismissJob,
    actionLoading: cancelLoading || dismissLoading,
    canSendByEmail,
    deliveryRequested,
    onSendByEmailHandler,
    sendByEmailLoading: requestDeliveryLoading,
    canRetry,
    onRetryHandler,
    retryLoading: requeueLoading,
  };
}
